<script>
import {PrismEditor} from "vue-prism-editor";
import "vue-prism-editor/dist/prismeditor.min.css";
import {highlight, languages} from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-css";
import "prismjs/components/prism-markup";
import "prismjs/themes/prism-tomorrow.css";
import exampleEmailHTML from "@/data/examples/whitelabel.email.html";
import {validateHTML} from "@/helpers/htmlValidator";

export default {
  components: {PrismEditor},
  data() {
    return {
      originalHTML: '',
    }
  },
  computed: {
    emailFooterTemplateHTML: {
      get() {
        return this.$store.state.whitelabel.email.custom;
      },
      set(value) {
        this.$store.state.whitelabel.email.custom = value;
      }
    },
    isModified() {
      return this.emailFooterTemplateHTML !== this.originalHTML;
    },
    isValidHTML() {
      return validateHTML(this.emailFooterTemplateHTML);
    }
  },
  mounted() {
    if(!this.emailFooterTemplateHTML) {
      this.emailFooterTemplateHTML = exampleEmailHTML;
    }
    this.originalHTML = this.emailFooterTemplateHTML;
  },
  methods: {
    htmlHighlighter(code) {
      return highlight(code, languages.html);
    },
    resetHTML() {
      this.emailFooterTemplateHTML = this.$store.state.whitelabel.email.example || exampleEmailHTML;
      this.originalHTML = this.emailFooterTemplateHTML;
    },
    save() {
      if(!validateHTML(this.emailFooterTemplateHTML)){
        this.$notify(this.$t('notification.html.invalid'), {type: 'error'});
        return;
      }
      this.$store.dispatch('putEmailHTML', this.emailFooterTemplateHTML);
      this.originalHTML = this.emailFooterTemplateHTML;
    }
  }
}
</script>

<template>
  <v-container fluid class="pt-0">
    <!--    HEADER-->
    <v-row class="pt-0">
      <v-col class="pt-0">
        <h3 class="float-left">{{ $t('component.whitelabel.custom.email.heading') }}</h3>
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn id="reset-template-html-button" class="float-right" icon color="primary" v-on="on" @click="resetHTML">
              <v-icon>mdi-autorenew</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('component.whitelabel.custom.email.reset.button.tooltip') }}</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-divider></v-divider>
    <!--   SUBHEADING-->
    <div class="d-flex align-center mt-3">
      <h4 class="text-subtitle-1 font-weight-bold me-2">
        {{ $t('component.whitelabel.custom.email.standard.footer') }}
      </h4>
      <v-tooltip top >
        <template #activator="{ on }">
          <v-icon
            class="text-info cursor-pointer"
            v-on="on">mdi-information</v-icon>
        </template>
        <span>{{ $t('component.whitelabel.custom.email.standard.footer.tooltip') }}</span>
      </v-tooltip>
    </div>

    <!--ERROR BANNER-->
    <v-row v-if="!isValidHTML" id="invalid-html-banner">
      <v-col>
        <v-fade-transition>
          <v-banner
            v-if="!isValidHTML"
            color="red"
            style="color: white"
            icon-color="white"
            icon="mdi-alert">
            <v-icon>mdi-warning</v-icon>
            {{ $t('component.whitelabel.custom.email.html.invalid') }}
          </v-banner>
        </v-fade-transition>
      </v-col>
    </v-row>

    <!--  EMAIL FOOTER HTML EDITOR-->
    <v-row>
      <v-col>
        <prism-editor
          id="whitelabel-email-html-footer-template"
          v-model="emailFooterTemplateHTML"
          class="html-editor"
          :disabled="!$store.getters.urls.subdomain"
          :highlight="htmlHighlighter"
          line-numbers></prism-editor>
      </v-col>
    </v-row>

    <!--    SAVE BUTTON-->
    <v-row v-if="$store.getters.urls.subdomain">
      <v-col>
        <v-fade-transition>
          <v-btn
            v-if="isModified"
            id="save-email-footer-template"
            color="primary"
            block
            :disabled="!isValidHTML"
            @click="save">
            <v-icon class="pe-3">mdi-check</v-icon>
            {{ $t('button.save') }}
          </v-btn>
        </v-fade-transition>
      </v-col>
    </v-row>
  </v-container>
</template>

<style>
.html-editor {
  background: #2d2d2d;
  color: #ccc;
  font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
  font-size: 14px;
  line-height: 1.5;
  padding: 5px;
}

.prism-editor__textarea:focus {
  outline: none;
}
</style>
